import React from 'react';
import { Link, withRouter } from 'react-router-dom';
import classNames from 'classnames';
import { SectionSplitProps } from '../../utils/SectionProps';
import ButtonGroup from '../elements/ButtonGroup';
import Button from '../elements/Button';
import Image from '../elements/Image';
import Modal from '../elements/Modal';
import HeroTransform from './HeroTransform';

import apiClient from '../../api/http';
import { API_ENDPOINTS } from '../../api/endpoints';
import { fetchLandingContent } from '../../api/landing';
import { fetchPlans } from '../../api/plans';
import { withLanguage } from '../../context/LanguageContext';
import { formatJstDate, jstDaysLeft, lastUsableDate } from '../../utils/licenseDates';

const propTypes = {
  ...SectionSplitProps.types
};

const defaultProps = {
  ...SectionSplitProps.defaults
};

class HeroSplit extends React.Component {
  state = {
    videoModalActive: false,
    license: null,
    loading: true,
    // Known synchronously on first render, so a logged-in user never sees the
    // "Free trial" button while /auth/me is still loading.
    hasToken: Boolean(localStorage.getItem('accessToken')),
    landingHero: null
  };

  /* =====================
     LIFECYCLE
     ===================== */
  componentDidMount() {
    Promise.all([fetchLandingContent(), fetchPlans()])
      .then(([landingRes, plansRes]) => {
        let heroData = null;
        if (landingRes?.success && landingRes.data?.hero) {
          heroData = { ...landingRes.data.hero };
          
          if (plansRes?.data?.data && plansRes.data.data.length > 0) {
            const minPrice = Math.min(
              ...plansRes.data.data
                .map(p => Number(p.monthlyPriceAnnual))
                .filter(p => !isNaN(p) && p > 0)
            );
            if (minPrice && isFinite(minPrice)) {
              const formattedPrice = minPrice.toLocaleString();
              heroData.subtitle = heroData.subtitle.replace(/月額([0-9,]+)円/, `月額${formattedPrice}円`);
            }
          }
          this.setState({ landingHero: heroData });
        }
      })
      .catch((err) => console.error("Failed to fetch landing data", err));

    const token = localStorage.getItem('accessToken');
    if (token) {
      this.fetchMe();
    } else {
      this.setState({ loading: false });
    }
  }

  /* =====================
     ME API
     ===================== */
  fetchMe = async () => {
    try {
      const res = await apiClient.get(API_ENDPOINTS.ME);
      this.setState({
        license: res?.data?.data?.license ?? null,
        loading: false
      });
    } catch (err) {
      // retry once (refresh-token timing)
      setTimeout(async () => {
        try {
          const retry = await apiClient.get(API_ENDPOINTS.ME);
          this.setState({
            license: retry?.data?.data?.license ?? null,
            loading: false
          });
        } catch {
          this.setState({ loading: false });
        }
      }, 800);
    }
  };

  getOcrStatus = () => {
    const { text, language } = this.props;
    const { license, loading } = this.state;

    if (loading) {
      return {
        expired: false,
        text: text('確認中...', 'Checking...')
      };
    }

    if (!license) {
      return {
        expired: true,
        text: text('ライセンス情報なし', 'No license information')
      };
    }

    // Last usable day and days left by JST calendar date (Findings #9).
    const daysRemaining = jstDaysLeft(license);

    if (!Number.isFinite(daysRemaining) || daysRemaining <= 0) {
      return {
        expired: true,
        text: text('ライセンス期限切れ', 'License expired')
      };
    }

    const expiryDate = formatJstDate(lastUsableDate(license), language);

    return {
      expired: false,
      text: text(`有効期限: ${expiryDate}（残り ${daysRemaining} 日）`, `Expires: ${expiryDate} (${daysRemaining} days remaining)`)
    };
  };

  render() {
    const { text, isEnglish } = this.props;
    // While /auth/me is loading, trust the stored token; once it returns,
    // fall back to the real license (an invalid token shows "Free trial").
    const isLoggedIn = Boolean(this.state.license) ||
      (this.state.loading && this.state.hasToken);
    const englishHero = {
      title: 'PITHIAS Extract',
      subtitle: 'A high-performance OCR platform powered entirely by generative AI and designed for end-to-end automation.'
    };

    const {
      className,
      topOuterDivider,
      bottomOuterDivider,
      topDivider,
      bottomDivider,
      hasBgColor,
      invertColor,
      invertMobile,
      invertDesktop,
      alignTop,
      ...props
    } = this.props;

    const outerClasses = classNames(
      'hero section',
      topOuterDivider && 'has-top-divider',
      bottomOuterDivider && 'has-bottom-divider',
      hasBgColor && 'has-bg-color',
      invertColor && 'invert-color',
      className
    );

    const innerClasses = classNames(
      'hero-inner section-inner',
      topDivider && 'has-top-divider',
      bottomDivider && 'has-bottom-divider'
    );

    const splitClasses = classNames(
      'split-wrap',
      invertMobile && 'invert-mobile',
      invertDesktop && 'invert-desktop',
      alignTop && 'align-top'
    );

    const ocr = this.getOcrStatus();

    return (
      <section {...props} className={outerClasses}>
        <div className="container">
          <div className={innerClasses}>
            <div className={splitClasses}>
              <div className="split-item">
                <div className="hero-content split-item-content center-content-mobile reveal-from-top">
                  {this.state.landingHero ? (
                    <>
                      <h3 
                        className="mt-0 mb-16" 
                        dangerouslySetInnerHTML={{ __html: isEnglish ? (this.state.landingHero.titleEn || englishHero.title) : this.state.landingHero.title }}
                      />
                      <p 
                        className="mt-0 mb-32" 
                        dangerouslySetInnerHTML={{ __html: isEnglish ? (this.state.landingHero.subtitleEn || englishHero.subtitle) : this.state.landingHero.subtitle }}
                      />
                    </>
                  ) : (
                    <>
                      <h3 className="mt-0 mb-16">
                        PITHIAS Extract
                        <br />
                        {text('(ピシアス エクストラクト)', '(PITHIAS Extract)')}
                      </h3>
                      <p className="mt-0 mb-32">
                        {text('PITHIAS Extractは、100%生成AIを前提とした完全自動化を目指す高性能OCRです。', 'PITHIAS Extract is a high-performance OCR platform powered entirely by generative AI and designed for end-to-end automation.')}
                      </p>
                    </>
                  )}

                  <ButtonGroup>
                    <Button
                      tag="a"
                      color="primary"
                      wideMobile
                      href="#pricing"
                    >
                      {text('価格とプラン', 'Pricing and plans')}
                    </Button>
                    {!isLoggedIn && (
                      <Link to="/signup" style={{ textDecoration: 'none' }}>
                        <Button color="primary" wideMobile>
                          {text('無料トライアル', 'Free trial')}
                        </Button>
                      </Link>
                    )}
                    {/* ===== OCR BUTTON (FIXED) ===== */}
                    {isLoggedIn && (
                      <div className="ocr-hover">
                        <Button
                          tag="button"
                          type="button"
                          wideMobile
                          id="tutorial-home-extract-btn"
                          className={`btn-extract ${ocr.expired ? 'ocr-expired' : ''}`}
                          onClick={() => {
                            if (!ocr.expired) {
                              this.props.history.push('/ocr-extract-view');
                            }
                          }}
                        >
                          {text('データ抽出実行', 'Run extraction')}
                        </Button>

                        <div className="ocr-tooltip">
                          {ocr.text}
                        </div>
                      </div>
                    )}
                  </ButtonGroup>
                </div>
                <div
                  className="hero-figure split-item-image reveal-from-bottom"
                  style={{
                    width: '100%',
                    maxWidth: '528px',
                    height: '396px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto'
                  }}
                >
                  {this.state.landingHero?.imagePath ? (
                    this.state.landingHero.imagePath.endsWith('.lottie') ? (
                      <dotlottie-wc
                        src={this.state.landingHero.imagePath}
                        autoplay
                        loop
                        style={{
                          width: '300px',
                          height: '300px',
                          transform: 'translate(12px, 10px)',
                          pointerEvents: 'none'
                        }}
                      ></dotlottie-wc>
                    ) : (
                      <img 
                        src={(this.state.landingHero.imagePath.startsWith('http') || this.state.landingHero.imagePath.startsWith('data:')) ? this.state.landingHero.imagePath : require(`./../../assets/images/${this.state.landingHero.imagePath}`)} 
                        alt="Hero Banner"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          pointerEvents: 'none',
                          borderRadius: '8px'
                        }}
                      />
                    )
                  ) : (
                    <HeroTransform />
                  )}
                </div>

                <Modal
                  id="video-modal"
                  show={this.state.videoModalActive}
                  handleClose={() =>
                    this.setState({ videoModalActive: false })
                  }
                  video="https://player.vimeo.com/video/174002812"
                  videoTag="iframe"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }
}

HeroSplit.propTypes = propTypes;
HeroSplit.defaultProps = defaultProps;

export default withRouter(withLanguage(HeroSplit));
